import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { tareTransfers, tareReturns, type TareTransferListItem } from '@/api/tare';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

type Kind = 'transfer' | 'return';

const DIR_LABEL: Record<string, string> = {
  TO_BUYER: 'Покупцю', FROM_SUPPLIER: 'Від постачальника',
  FROM_BUYER: 'Від покупця', TO_SUPPLIER: 'Постачальнику',
};

/** Список документів зворотної тари (Порція C). Перехід на TareEditor. */
export default function TarePage({ kind: kindProp }: { kind?: Kind } = {}) {
  const navigate = useNavigate();
  // kind визначається пропом з роуту (transfer/return).
  const kind: Kind = kindProp ?? 'transfer';
  const [orgId, setOrgId] = useState('');
  const api = kind === 'transfer' ? tareTransfers : tareReturns;
  const basePath = kind === 'transfer' ? '/tare-transfers' : '/tare-returns';
  const title = kind === 'transfer' ? 'Передача тари (застава)' : 'Повернення тари';

  const orgList = useQuery({ queryKey: ['orgs-tare'], queryFn: () => orgs.list({ limit: 200 }) });
  const listQ = useQuery({
    queryKey: ['tare', kind, orgId],
    queryFn: () => api.list({ organization_id: orgId || undefined }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<TareTransferListItem>[]>(() => [
    {
      key: 'document_number', header: 'Номер', width: 150, importBadge: true,
      sortValue: (d) => d.document_number ?? '', cell: (d) => d.document_number,
    },
    {
      key: 'document_date', header: 'Дата', width: 120,
      sortValue: (d) => d.document_date ?? '', cell: (d) => d.document_date?.slice(0, 10),
    },
    {
      key: 'direction', header: 'Напрям', width: 180,
      sortValue: (d) => DIR_LABEL[d.direction] ?? d.direction,
      cell: (d) => DIR_LABEL[d.direction] ?? d.direction,
    },
    {
      key: 'total_amount', header: 'Сума застави', width: 160, align: 'right',
      sortValue: (d) => Number(d.total_amount) || 0, cell: (d) => d.total_amount,
    },
    {
      key: 'state', header: 'Стан', width: 150,
      sortValue: (d) => (d.posted ? '✓ проведено' : d.state),
      cell: (d) => (d.posted ? '✓ проведено' : d.state),
    },
  ], []);

  return (
    <div style={{ padding: '1rem' }}>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Зворотна тара — {title}</h2>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <label>Юрособа{' '}
            <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
              <option value="">— оберіть —</option>
              {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
          <button className="primary" disabled={!orgId}
            onClick={() => navigate(`${basePath}/new`)}>+ Новий документ</button>
        </div>
      </div>

      <div style={{ marginBottom: '0.75rem' }}>
        <button className={kind === 'transfer' ? 'active' : ''}
          onClick={() => navigate('/tare-transfers')}>Передача</button>
        <button className={kind === 'return' ? 'active' : ''}
          onClick={() => navigate('/tare-returns')} style={{ marginLeft: '0.5rem' }}>Повернення</button>
      </div>

      {!orgId && <p style={{ color: 'var(--muted)' }}>Оберіть юрособу для перегляду документів тари.</p>}

      {orgId && (
        <DataTable<TareTransferListItem>
          rows={listQ.data?.items ?? []}
          columns={cols}
          rowKey={(d) => d.id}
          loading={listQ.isLoading}
          emptyText="Документів немає."
          onRowDoubleClick={(d) => navigate(`${basePath}/${d.id}`)}
          initialSortKey="document_number"
        />
      )}
    </div>
  );
}
